import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  useTheme,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import ConfirmDialog from "components/ConfirmDialog";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import CommunicationServices from "services/CommunicationServices";
import { downloadCommunicationAttachment } from "services/AttachmentServices";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import TableChartIcon from "@mui/icons-material/TableChart";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArchiveIcon from "@mui/icons-material/Archive";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieIcon from "@mui/icons-material/Movie";

const ChatContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e8edf3"
    }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 24px rgba(0,0,0,0.3)"
      : "0 4px 24px rgba(2,89,165,0.07)",
  overflow: "hidden",
  transition: "all 0.3s ease",
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75, 2.5),
  background:
    theme.palette.mode === "dark"
      ? theme.palette.sidebar.background
      : "#ffffff",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#edf1f7"
    }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  height: 420,
  overflowY: "auto",
  padding: theme.spacing(2),
  background:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : "#f5f7fa",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,0,0,0.12)",
    borderRadius: 2,
  },
}));

const MessageBubble = styled(Box)(({ theme, isown }) => ({
  maxWidth: "88%",
  padding: theme.spacing(1.25, 1.75),
  borderRadius:
    isown === "true" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
  background:
    isown === "true"
      ? theme.palette.mode === "dark"
        ? "linear-gradient(135deg, #1e3a5f, #2c5282)"
        : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
      : theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "#ffffff",
  color: isown === "true" ? "#ffffff" : theme.palette.text.primary,
  boxShadow:
    isown === "true"
      ? `0 2px 12px ${theme.palette.primary.main}40`
      : theme.palette.mode === "dark"
        ? "0 1px 6px rgba(0,0,0,0.25)"
        : "0 1px 6px rgba(0,0,0,0.07)",
  border:
    isown === "true"
      ? "none"
      : `1px solid ${theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "#edf1f7"
      }`,
  wordBreak: "break-word",
  position: "relative",
  transition: "transform 0.15s ease",
  "&:hover": {
    transform: "translateY(-1px)",
  },
}));

const ContributionChatPanel = ({
  contributionId,
  isOpen,
  onToggle,
  onMessageCountUpdate,
  currentUser: propCurrentUser,
  topicStatus,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const reduxCurrentUser = useSelector((state) => state.auth.user);
  const currentUser = propCurrentUser || reduxCurrentUser;

  // Check if current user is admin
  const isAdmin =
    currentUser?.roles?.some((role) => role?.toLowerCase() === "admin") ||
    false;

  // Check if topic is active
  const isTopicActive = topicStatus === "active";

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  // Edit message states
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Menu states
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  };

  const fetchMessages = async (page = 1) => {
    if (!contributionId || !isOpen) return;

    setIsLoading(true);
    try {
      const response = await CommunicationServices.getByContributionId(
        contributionId,
        {
          page,
          limit: 20,
        }
      );

      if (response.success) {
        const newMessages = response.data?.data?.data || [];

        console.log("response", response);

        if (page === 1) {
          setMessages(newMessages);
          // Update message count for parent component
          if (onMessageCountUpdate) {
            onMessageCountUpdate(newMessages.length);
          }
        } else {
          setMessages((prev) => [...prev, ...newMessages]);
        }

        // Pagination info available in response.data if needed later

        if (page === 1) {
          setTimeout(scrollToBottom, 100);
        }
      } else {
        toast.error(t("chatPanel.failedToLoadMessages"));
      }
    } catch (error) {
      toast.error(t("chatPanel.errorLoadingMessages"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMessages(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contributionId, isOpen]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) {
      toast.warning(t("chatPanel.enterMessageOrAttachFiles"));
      return;
    }

    setIsSending(true);
    try {
      const formData = new FormData();
      if (newMessage.trim()) {
        formData.append("content", newMessage.trim());
      }

      selectedFiles.forEach((file) => {
        formData.append("attachments", file);
      });

      const response = await CommunicationServices.createMessage(
        contributionId,
        formData
      );

      if (response.success) {
        setNewMessage("");
        setSelectedFiles([]);
        setJustSent(true);
        setTimeout(() => setJustSent(false), 2000); // Reset after 2 seconds
        fetchMessages(1); // Refresh messages and update count
        toast.success(t("chatPanel.responseSubmittedSuccessfully"));
      } else {
        toast.error(response.message || t("chatPanel.failedToSendMessage"));
      }
    } catch (error) {
      toast.error(t("chatPanel.errorSendingMessage"));
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + selectedFiles.length > 5) {
      toast.warning(t("chatPanel.maximumFilesAllowed"));
      return;
    }

    setSelectedFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileRemove = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMessageMenuOpen = (event, message) => {
    setMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMessageMenuClose = () => {
    setMenuAnchor(null);
    setSelectedMessage(null);
  };

  const handleEditMessage = () => {
    setEditingMessage(selectedMessage);
    setEditContent(selectedMessage.content || "");
    setEditFiles([]);
    setIsEditDialogOpen(true);
    handleMessageMenuClose();
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    ConfirmDialog({
      type: "error",
      title: t("confirmDialog.deleteMessage"),
      description: t("confirmDialog.deleteMessageDescription"),
      buttons: [
        {
          label: t("buttons.cancel"),
          className: "custom-confirm-dialog__btn--cancel",
        },
        {
          label: t("buttons.delete"),
          className: "custom-confirm-dialog__btn--danger",
          onClick: () => confirmDeleteMessage(),
        },
      ],
    });
  };

  const confirmDeleteMessage = async () => {
    if (!selectedMessage) return;

    try {
      const response = await CommunicationServices.deleteMessage(
        selectedMessage.id
      );
      if (response.success) {
        toast.success(t("chatPanel.messageDeletedSuccessfully"));
        fetchMessages(1); // Refresh messages and update count
      } else {
        toast.error(response.message || t("chatPanel.failedToDeleteMessage"));
      }
    } catch (error) {
      toast.error(t("chatPanel.errorDeletingMessage"));
    } finally {
      handleMessageMenuClose();
    }
  };

  const handleUpdateMessage = async () => {
    if (!editContent.trim() && editFiles.length === 0) {
      toast.warning(t("chatPanel.enterContentOrAttachFiles"));
      return;
    }

    try {
      const formData = new FormData();
      if (editContent.trim()) {
        formData.append("content", editContent.trim());
      }

      editFiles.forEach((file) => {
        formData.append("attachments", file);
      });

      const response = await CommunicationServices.updateMessage(
        editingMessage.id,
        formData
      );

      if (response.success) {
        toast.success(t("chatPanel.messageUpdatedSuccessfully"));
        setIsEditDialogOpen(false);
        setEditingMessage(null);
        setEditContent("");
        setEditFiles([]);
        fetchMessages(1); // Refresh messages and update count
      } else {
        toast.error(response.message || t("chatPanel.failedToUpdateMessage"));
      }
    } catch (error) {
      toast.error(t("chatPanel.errorUpdatingMessage"));
    }
  };

  const handleDownloadAttachment = async (messageId, attachment) => {
    try {
      await downloadCommunicationAttachment(messageId, attachment);
    } catch (error) {
      toast.error(t("chatPanel.errorDownloadingAttachment"));
    }
  };

  const getFileExtension = (filename = "") => {
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  };

  const getAttachmentIcon = (attachment) => {
    const mimetype = attachment?.mimetype || "";
    const ext = getFileExtension(
      attachment?.filename || attachment?.originalname || ""
    );

    if (mimetype.startsWith("image/"))
      return { Icon: ImageIcon, color: "info.main" };
    if (mimetype.startsWith("audio/"))
      return { Icon: AudiotrackIcon, color: "secondary.main" };
    if (mimetype.startsWith("video/"))
      return { Icon: MovieIcon, color: "warning.main" };
    if (mimetype === "application/pdf")
      return { Icon: PictureAsPdfIcon, color: "error.main" };

    switch (ext) {
      case "pdf":
        return { Icon: PictureAsPdfIcon, color: "error.main" };
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
      case "webp":
        return { Icon: ImageIcon, color: "info.main" };
      case "doc":
      case "docx":
        return { Icon: DescriptionIcon, color: "primary.main" };
      case "xls":
      case "xlsx":
      case "csv":
        return { Icon: TableChartIcon, color: "success.main" };
      case "ppt":
      case "pptx":
        return { Icon: SlideshowIcon, color: "warning.main" };
      case "txt":
      case "md":
        return { Icon: TextSnippetIcon, color: "text.secondary" };
      case "zip":
      case "rar":
      case "7z":
        return { Icon: ArchiveIcon, color: "secondary.main" };
      case "mp3":
      case "wav":
        return { Icon: AudiotrackIcon, color: "secondary.main" };
      case "mp4":
      case "mov":
      case "avi":
        return { Icon: MovieIcon, color: "warning.main" };
      default:
        return { Icon: InsertDriveFileIcon, color: "primary.main" };
    }
  };

  const canEditDelete = (message) => {
    return isAdmin || currentUser?.id === message.sender_id;
  };

  const messageCountLabel = t("chatPanel.messageCount", {
    count: messages.length,
  });

  const attachedFilesLabel = (count) =>
    t("chatPanel.attachedFilesCount", { count });

  if (!isOpen) return null;

  return (
    <ChatContainer sx={{ mt: 2 }}>
      {/* ── Header ── */}
      <ChatHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(100, 181, 246, 0.15)"
                  : `${theme.palette.primary.main}14`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <QuestionAnswerIcon
              sx={{ fontSize: 20, color: theme.palette.primary.main }}
            />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                lineHeight: 1.2,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {t("chatPanel.discussion")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {messageCountLabel}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor:
              messages.length > 0
                ? "success.main"
                : theme.palette.text.disabled,
            boxShadow:
              messages.length > 0
                ? "0 0 0 3px rgba(76,175,80,0.2)"
                : "none",
            transition: "all 0.3s ease",
          }}
        />
      </ChatHeader>

      {/* ── Messages Area ── */}
      <MessagesContainer>
        {isLoading && messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
            }}
          >
            <CircularProgress
              size={30}
              sx={{ color: theme.palette.primary.main }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {t("chatPanel.loadingMessages")}
            </Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              p: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(100, 181, 246, 0.1)"
                    : `${theme.palette.primary.main}10`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
                border: `2px dashed ${theme.palette.primary.main}35`,
              }}
            >
              <QuestionAnswerIcon
                sx={{
                  fontSize: 36,
                  color: theme.palette.primary.main,
                  opacity: 0.5,
                }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.75,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {isAdmin
                ? t("chatPanel.requestClarification")
                : t("chatPanel.noCommunicationHistory")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 260,
                lineHeight: 1.6,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {isAdmin
                ? t("chatPanel.contributionInfoChannel")
                : t("chatPanel.adminRequestsAppearHere")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {messages.map((message, index) => {
              const isAdminMessage = Boolean(
                message.sender?.type?.toLowerCase() === "admin" ||
                message.sender?.roles?.some(
                  (role) => role?.toLowerCase() === "admin"
                )
              );
              const showDateSeparator =
                index === 0 ||
                moment(messages[index - 1]?.created_at).format(
                  "YYYY-MM-DD"
                ) !== moment(message.created_at).format("YYYY-MM-DD");

              return (
                <Box
                  key={message.id}
                  sx={{
                    animation: "fadeIn 0.25s ease-out",
                    animationDelay: `${Math.min(index * 30, 200)}ms`,
                    animationFillMode: "backwards",
                    "@keyframes fadeIn": {
                      "0%": { opacity: 0, transform: "translateY(8px)" },
                      "100%": { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                  {/* Date separator */}
                  {showDateSeparator && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        my: 2.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(100, 181, 246, 0.12)"
                              : `${theme.palette.primary.main}12`,
                          color: theme.palette.primary.main,
                          px: 2,
                          py: 0.5,
                          borderRadius: "20px",
                          fontWeight: 500,
                          fontSize: "11px",
                          fontFamily: theme.typography.fontFamily,
                        }}
                      >
                        {moment(message.created_at).calendar(null, {
                          sameDay: `[${t("chatPanel.today")}]`,
                          lastDay: `[${t("chatPanel.yesterday")}]`,
                          lastWeek: "dddd",
                          sameElse: "MMM DD, YYYY",
                        })}
                      </Typography>
                    </Box>
                  )}

                  {/* Message row: avatar + bubble */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isAdminMessage ? "row-reverse" : "row",
                      alignItems: "flex-end",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    {/* Avatar */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: isAdminMessage
                          ? theme.palette.primary.main
                          : theme.palette.secondary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#fff",
                        boxShadow: isAdminMessage
                          ? `0 2px 8px ${theme.palette.primary.main}40`
                          : `0 2px 8px ${theme.palette.secondary.main}40`,
                      }}
                    >
                      {(message.sender?.name?.[0] || "?").toUpperCase()}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: isAdminMessage ? "flex-end" : "flex-start",
                        maxWidth: "calc(100% - 44px)",
                      }}
                    >
                      {/* Sender name + admin badge */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: isAdminMessage
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          mb: 0.4,
                          px: 0.5,
                          fontSize: "11px",
                          fontFamily: theme.typography.fontFamily,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        {message.sender?.name || t("chatPanel.unknown")}
                        {isAdminMessage && (
                          <Box
                            component="span"
                            sx={{
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 0.75,
                              fontSize: "9px",
                              fontWeight: 700,
                              bgcolor: theme.palette.primary.main,
                              color: "#fff",
                              letterSpacing: 0.3,
                              textTransform: "uppercase",
                              lineHeight: 1.6,
                            }}
                          >
                            {t("chatPanel.admin")}
                          </Box>
                        )}
                      </Typography>

                      {/* Bubble */}
                      <MessageBubble isown={isAdminMessage.toString()}>
                        {message.content && (
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.55,
                              fontSize: "14px",
                              color: "inherit",
                              fontFamily: theme.typography.fontFamily,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {message.content}
                          </Typography>
                        )}

                        {/* Attachments inside bubble */}
                        {message.attachments &&
                          message.attachments.length > 0 && (
                            <Box
                              sx={{
                                mt: message.content ? 1.25 : 0,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                              }}
                            >
                              {message.attachments.map((attachment, idx) => {
                                const { Icon, color } =
                                  getAttachmentIcon(attachment);
                                const [pk, sk] = (
                                  color || "primary.main"
                                ).split(".");
                                const iconColor = isAdminMessage
                                  ? "rgba(255,255,255,0.9)"
                                  : theme.palette?.[pk]?.[sk] ||
                                  theme.palette.primary.main;
                                return (
                                  <Box
                                    key={idx}
                                    onClick={() =>
                                      handleDownloadAttachment(
                                        message.id,
                                        attachment
                                      )
                                    }
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      px: 1.25,
                                      py: 0.875,
                                      borderRadius: 1.5,
                                      cursor: "pointer",
                                      bgcolor: isAdminMessage
                                        ? "rgba(255,255,255,0.12)"
                                        : theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.05)"
                                          : "rgba(0,0,0,0.04)",
                                      border: `1px solid ${isAdminMessage
                                          ? "rgba(255,255,255,0.2)"
                                          : theme.palette.divider
                                        }`,
                                      transition: "all 0.15s ease",
                                      "&:hover": {
                                        bgcolor: isAdminMessage
                                          ? "rgba(255,255,255,0.2)"
                                          : theme.palette.action.hover,
                                        transform: "translateX(2px)",
                                      },
                                    }}
                                  >
                                    <Icon
                                      sx={{
                                        fontSize: 18,
                                        color: iconColor,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          fontWeight: 600,
                                          color: isAdminMessage
                                            ? "rgba(255,255,255,0.95)"
                                            : theme.palette.text.primary,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          fontSize: "11px",
                                          fontFamily:
                                            theme.typography.fontFamily,
                                        }}
                                      >
                                        {attachment.originalname ||
                                          attachment.filename}
                                      </Typography>
                                      {attachment.size && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "10px",
                                            color: isAdminMessage
                                              ? "rgba(255,255,255,0.65)"
                                              : theme.palette.text.secondary,
                                            fontFamily:
                                              theme.typography.fontFamily,
                                          }}
                                        >
                                          {(attachment.size / 1024).toFixed(1)}{" "}
                                          KB
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                        {/* Timestamp + options menu */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 0.75,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.65,
                              fontSize: "10px",
                              color: "inherit",
                              fontFamily: theme.typography.fontFamily,
                            }}
                          >
                            {moment(message.created_at).format("h:mm A")}
                            {message.updated_at &&
                              message.updated_at !== message.created_at && (
                                <Box
                                  component="span"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  {" "}
                                  · edited
                                </Box>
                              )}
                          </Typography>
                          {canEditDelete(message) && (
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleMessageMenuOpen(e, message)
                              }
                              sx={{
                                ml: 0.5,
                                color: "inherit",
                                opacity: 0.45,
                                "&:hover": { opacity: 1 },
                                width: 18,
                                height: 18,
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      </MessageBubble>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </MessagesContainer>

      {/* ── Input area (admin always, others when messages exist) ── */}
      {(isAdmin || messages.length > 0) && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "#edf1f7"
              }`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          {/* File attachment preview */}
          {selectedFiles.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 2,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(100, 181, 246, 0.05)"
                    : "rgba(2, 89, 165, 0.03)",
                border: `1px solid ${theme.palette.primary.main}30`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  mb: 1,
                  display: "block",
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                {attachedFilesLabel(selectedFiles.length)}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedFiles.map((file, index) => {
                  const { Icon, color } = getAttachmentIcon({
                    mimetype: file.type,
                    filename: file.name,
                  });
                  const [paletteKey, shadeKey] = (
                    color || "primary.main"
                  ).split(".");
                  const iconColor =
                    theme.palette?.[paletteKey]?.[shadeKey] ||
                    theme.palette.primary.main;
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        maxWidth: 200,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: iconColor,
                          boxShadow: `0 2px 8px ${iconColor}22`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: `${iconColor}18`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 16, color: iconColor }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "11px",
                            lineHeight: 1.3,
                          }}
                          title={file.name}
                        >
                          {file.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "10px",
                            lineHeight: 1.2,
                          }}
                        >
                          {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleFileRemove(index)}
                        sx={{
                          width: 18,
                          height: 18,
                          flexShrink: 0,
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            color: theme.palette.error.main,
                            bgcolor: `${theme.palette.error.main}18`,
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Composer bar */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: "none" }}
            accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx"
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 0,
              borderRadius: "24px",
              border: `1.5px solid`,
              borderColor: theme.palette.divider,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "#f8fafc",
              overflow: "hidden",
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              "&:focus-within": {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
                bgcolor: theme.palette.background.paper,
              },
            }}
          >
            {/* Attach button — left inside pill */}
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={!isTopicActive || selectedFiles.length >= 5}
              size="small"
              sx={{
                m: "6px 2px 6px 6px",
                width: 34,
                height: 34,
                borderRadius: "50%",
                flexShrink: 0,
                color: theme.palette.text.secondary,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: theme.palette.primary.main,
                  bgcolor: `${theme.palette.primary.main}14`,
                },
                "&.Mui-disabled": {
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <AttachFileIcon sx={{ fontSize: 19 }} />
            </IconButton>

            {/* Text input */}
            <TextField
              fullWidth
              multiline
              maxRows={5}
              placeholder={
                isTopicActive
                  ? t("chatPanel.typeYourMessage")
                  : t("chatPanel.messagingDisabledInactiveTopic")
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={!isTopicActive}
              variant="standard"
              sx={{
                "& .MuiInput-root": {
                  fontSize: "14px",
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.text.primary,
                  "&:before, &:after": { display: "none" },
                  py: "10px",
                },
                "& .MuiInput-input": {
                  padding: 0,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "14px",
                  lineHeight: 1.5,
                  "&::placeholder": {
                    color:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.3)"
                        : "#9aabbf",
                    opacity: 1,
                  },
                },
              }}
            />

            {/* Send button — right inside pill */}
            <Box sx={{ m: "6px 6px 6px 2px", flexShrink: 0 }}>
              <IconButton
                onClick={handleSendMessage}
                disabled={
                  !isTopicActive ||
                  isSending ||
                  (!newMessage.trim() && selectedFiles.length === 0)
                }
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  bgcolor:
                    !isTopicActive ||
                      (!newMessage.trim() && selectedFiles.length === 0)
                      ? "transparent"
                      : theme.palette.primary.main,
                  color:
                    !isTopicActive ||
                      (!newMessage.trim() && selectedFiles.length === 0)
                      ? theme.palette.text.disabled
                      : "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                    color: "#fff",
                    transform: "scale(1.08)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "transparent",
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {isSending ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : (
                  <SendIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Footer for non-admin with no messages ── */}
      {!isAdmin && messages.length === 0 && (
        <Box
          sx={{
            p: 1.75,
            borderTop: `1px solid ${theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "#edf1f7"
              }`,
            bgcolor: theme.palette.background.paper,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: theme.palette.text.disabled,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily,
              fontSize: "12px",
            }}
          >
            {t("chatPanel.adminRequestsAppearHere")}
          </Typography>
        </Box>
      )}

      {/* ── Message actions context menu ── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMessageMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 16px rgba(0,0,0,0.4)"
                : "0 4px 16px rgba(0,0,0,0.15)",
            border: `1px solid ${theme.palette.mode === "dark"
                ? theme.palette.sidebar.divider
                : "#e3eaf3"
              }`,
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={handleEditMessage}
          sx={{
            fontFamily: theme.typography.fontFamily,
            "&:hover": {
              backgroundColor: `${theme.palette.primary.main}10`,
            },
          }}
        >
          <EditIcon
            sx={{ mr: 1.5, fontSize: 18, color: theme.palette.primary.main }}
          />
          {t("chatPanel.editResponse")}
        </MenuItem>
        <MenuItem
          onClick={handleDeleteMessage}
          sx={{
            color: theme.palette.error.main,
            fontFamily: theme.typography.fontFamily,
            "&:hover": {
              backgroundColor: `${theme.palette.error.main}10`,
            },
          }}
        >
          <DeleteIcon sx={{ mr: 1.5, fontSize: 18 }} />
          {t("chatPanel.deleteResponse")}
        </MenuItem>
      </Menu>

      {/* ── Edit message dialog ── */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontWeight: 600,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.mode === "dark"
                ? theme.palette.sidebar.divider
                : "#e3eaf3"
              }`,
          }}
        >
          {t("chatPanel.editResponse")}
          <IconButton
            onClick={() => setIsEditDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: `${theme.palette.primary.main}10`,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t("chatPanel.responseContent")}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            sx={{
              mb: 2,
              mt: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontFamily: theme.typography.fontFamily,
              },
              "& .MuiInputLabel-root": {
                fontFamily: theme.typography.fontFamily,
              },
            }}
          />

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => editFileInputRef.current?.click()}
            disabled={editFiles.length >= 5}
            sx={{
              mb: 2,
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {t("chatPanel.attachFilesCount", { count: editFiles.length })}
          </Button>

          <input
            type="file"
            ref={editFileInputRef}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length + editFiles.length > 5) {
                toast.warning(t("chatPanel.maximumFilesAllowed"));
                return;
              }
              setEditFiles((prev) => [...prev, ...files]);
              e.target.value = "";
            }}
            multiple
            style={{ display: "none" }}
            accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx"
          />

          {editFiles.length > 0 && (
            <List dense>
              {editFiles.map((file, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() =>
                        setEditFiles((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            gap: 1,
            borderTop: `1px solid ${theme.palette.mode === "dark"
                ? theme.palette.sidebar.divider
                : "#e3eaf3"
              }`,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setIsEditDialogOpen(false)}
            sx={{ fontFamily: theme.typography.fontFamily }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUpdateMessage}
            disabled={!editContent.trim() && editFiles.length === 0}
            sx={{ fontFamily: theme.typography.fontFamily }}
          >
            {t("chatPanel.updateResponse")}
          </Button>
        </DialogActions>
      </Dialog>
    </ChatContainer>
  );
};

export default ContributionChatPanel;
